import { useState, useEffect, useCallback } from 'react'
import { listApiKeys, createApiKey, deleteApiKey } from '../services/api'
import ApiKeyCreatedModal from './ApiKeyCreatedModal'

function ApiKeyManager({ address, onBack }) {
  const [apiKeys, setApiKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newKeyAlias, setNewKeyAlias] = useState('')
  const [creating, setCreating] = useState(false)
  const [createdKey, setCreatedKey] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const loadApiKeys = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await listApiKeys(address.email)
      setApiKeys(response.api_keys || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [address.email])

  useEffect(() => {
    loadApiKeys()
  }, [loadApiKeys])

  const handleCreateKey = async (e) => {
    e.preventDefault()
    try {
      setCreating(true)
      setError(null)
      const response = await createApiKey(address.email, newKeyAlias || 'Default')
      setCreatedKey(response)
      setShowCreateModal(false)
      setNewKeyAlias('')
      await loadApiKeys()
    } catch (err) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteKey = async (keyId) => {
    try {
      setDeleting(true)
      setError(null)
      await deleteApiKey(address.email, keyId)
      setDeleteConfirm(null)
      await loadApiKeys()
    } catch (err) {
      setError(err.message)
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Addresses
          </button>
          <h2 className="mt-2 text-xl font-semibold text-gray-900">API Keys</h2>
          <p className="text-sm text-gray-500 font-mono">{address.email}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Create API Key
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <svg className="h-8 w-8 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No API Keys</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create an API key to access emails for this address via the API.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {apiKeys.map((key) => (
            <div
              key={key.key_id}
              className="rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{key.alias}</h3>
                  <div className="mt-1 space-y-1 text-sm text-gray-500">
                    <p>Created: {formatDate(key.created_at)}</p>
                    <p>Last used: {formatDate(key.last_used_at)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setDeleteConfirm(key)}
                  className="rounded-md bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Create API Key</h3>
            <form onSubmit={handleCreateKey} className="mt-4">
              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-700">
                  Key Name (Alias)
                </label>
                <input
                  type="text"
                  id="alias"
                  value={newKeyAlias}
                  onChange={(e) => setNewKeyAlias(e.target.value)}
                  placeholder="e.g., Production, Testing, CI/CD"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    setNewKeyAlias('')
                  }}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900">Delete API Key</h3>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to delete the API key &quot;{deleteConfirm.alias}&quot;?
              This action cannot be undone and any applications using this key will lose access.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteKey(deleteConfirm.key_id)}
                disabled={deleting}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key Created Modal */}
      {createdKey && (
        <ApiKeyCreatedModal
          apiKey={createdKey.api_key}
          alias={createdKey.alias}
          onClose={() => setCreatedKey(null)}
        />
      )}
    </div>
  )
}

export default ApiKeyManager
